import React, { useState } from 'react';
import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { interpolateColor } from '../utils/energiehuisje';

// Buurtstatus = de "Markeer voor akkoord"-cyclus per locatie (akkoord_status op
// de gekozen/nieuwste versie). Eén bron van waarheid: hetzelfde palet kleurt de
// stip op de kaart, de swatch in de stationslijst en deze legenda. De kleuren
// sluiten aan op de knopkleuren in VersieTijdlijn (teal/rood/grijs).
export const BUURT_STATUSSEN = [
  { key: 'akkoord', label: 'Akkoord', kleur: '#39e600' },
  { key: 'afgekeurd', label: 'Afgekeurd', kleur: '#e03131' },
  { key: 'vervallen', label: 'Vervallen', kleur: '#000000' },
  { key: '', label: 'Nog te bepalen', kleur: '#5dade2' },
];

// Soort werk = de rand van de stip. Nieuw/ombouw/vervangend volgen uit het
// projectveld `type`; "te vervangen" is een bestaand station dat aan een
// vervangend station gekoppeld is.
export const BUURT_SOORTEN = [
  { label: 'Nieuw station', rand: '#ffffff' },
  { label: 'Ombouw', rand: '#868e96' },
  { label: 'Vervangend station', rand: '#1971c2' },
  { label: 'Te vervangen station', rand: '#e03131', vulling: '#888' },
];

// Kleur voor een gegeven akkoord_status (leeg/onbekend → 'Nog te bepalen').
export function akkoordKleur(status) {
  const s = BUURT_STATUSSEN.find(x => x.key === (status || ''));
  return (s || BUURT_STATUSSEN[BUURT_STATUSSEN.length - 1]).kleur;
}

// KLIC/IMKL-kleurcodering per thema, exact zoals de landelijke KlicBeheer-config
// (api/configuration/mapbox-layers, match op ['get','thema']). Hiermee is op
// projectniveau te zien welke kleur welke kabel/leiding betekent.
export const KLIC_THEMAS = [
  { thema: 'laagspanning', label: 'Laagspanning', kleur: '#960000' },
  { thema: 'middenspanning', label: 'Middenspanning', kleur: '#c80000' },
  { thema: 'hoogspanning', label: 'Hoogspanning', kleur: '#ff0000' },
  { thema: 'landelijkHoogspanningsnet', label: 'Landelijk hoogspanningsnet', kleur: '#ff0000' },
  { thema: 'gasLageDruk', label: 'Gas (lage druk)', kleur: '#ffd750' },
  { thema: 'gasHogeDruk', label: 'Gas (hoge druk)', kleur: '#ffaf3c' },
  { thema: 'water', label: 'Water', kleur: '#0000ff' },
  { thema: 'rioolVrijverval', label: 'Riool (vrijverval)', kleur: '#ba38a8' },
  { thema: 'rioolOnderOverOfOnderdruk', label: 'Riool (onder over-/onderdruk)', kleur: '#800080' },
  { thema: 'warmte', label: 'Warmte', kleur: '#008080' },
  { thema: 'datatransport', label: 'Datatransport', kleur: '#00ff00' },
  { thema: 'petrochemie', label: 'Petrochemie', kleur: '#b64a00' },
  { thema: 'buisleidingGevaarlijkeInhoud', label: 'Buisleiding gevaarlijke inhoud', kleur: '#ff7f00' },
  { thema: 'wees', label: 'Wees (onbekende beheerder)', kleur: '#918a6f' },
];

export function jaarKleur(jaar, minJaar, maxJaar) {
  const t = maxJaar === minJaar ? 0 : (jaar - minJaar) / (maxJaar - minJaar);
  if (t <= 0.25) return interpolateColor('#377eb8', '#4daf4a', t / 0.25);
  if (t <= 0.5) return interpolateColor('#4daf4a', '#ffff33', (t - 0.25) / 0.25);
  if (t <= 0.75) return interpolateColor('#ffff33', '#ff7f00', (t - 0.5) / 0.25);
  return interpolateColor('#ff7f00', '#e41a1c', (t - 0.75) / 0.25);
}

function JarenLegenda({ jaren, minJaar, maxJaar, selectedYear, onYearClick }) {
  const uniekeJaren = Array.from(new Set(jaren)).sort((a, b) => a - b);
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600}>Start uitvoering (klik = filter)</Text>
      <Stack gap={3}>
        {uniekeJaren.map(jaar => {
          const kleur = jaarKleur(jaar, minJaar, maxJaar);
          const actief = selectedYear === jaar;
          return (
            <Group
              key={jaar}
              className="legend-year"
              gap={8} wrap="nowrap"
              onClick={() => onYearClick(jaar)}
              style={{
                cursor: 'pointer', padding: '2px 6px', borderRadius: 4, userSelect: 'none',
                background: actief ? 'var(--mantine-color-gray-1)' : 'transparent',
                border: actief ? '1px solid #000' : '1px solid transparent',
              }}
            >
              <span style={{
                width: 16, height: 16, background: kleur, borderRadius: 3,
                border: '1px solid #bbb', flexShrink: 0,
              }} />
              <Text size="xs" fw={actief ? 700 : 400}>{jaar}</Text>
            </Group>
          );
        })}
      </Stack>
    </Stack>
  );
}

// Eén stip in de legenda: vulling = status, rand = soort.
function Stip({ vulling, rand }) {
  return (
    <span style={{
      display: 'inline-block', width: 13, height: 13, borderRadius: '50%',
      background: vulling, border: `2px solid ${rand}`, boxShadow: '0 0 0 1px #ddd',
      flexShrink: 0,
    }} />
  );
}

// Buurtniveau: de stip codeert twee dingen tegelijk. De vulling is de
// akkoordstatus (de "Markeer voor akkoord"-cyclus), de rand het soort werk.
function StatusLegenda() {
  return (
    <Stack gap={8}>
      <Stack gap={4}>
        <Text size="xs" fw={600}>Status (vulling)</Text>
        {BUURT_STATUSSEN.map(({ label, kleur }) => (
          <Group key={label} gap={6} wrap="nowrap">
            <Stip vulling={kleur} rand="#fff" />
            <Text size="xs">{label}</Text>
          </Group>
        ))}
      </Stack>
      <Stack gap={4}>
        <Text size="xs" fw={600}>Soort (rand)</Text>
        {BUURT_SOORTEN.map(({ label, rand, vulling }) => (
          <Group key={label} gap={6} wrap="nowrap">
            <Stip vulling={vulling || '#5dade2'} rand={rand} />
            <Text size="xs">{label}</Text>
          </Group>
        ))}
        <Group gap={6} wrap="nowrap">
          <Stip vulling="#888" rand="#fff" />
          <Text size="xs">Bestaand station</Text>
        </Group>
      </Stack>
    </Stack>
  );
}

// Projectniveau: KLIC-kleurcodering van kabels/leidingen per thema. Alleen de
// thema's die in de geladen buurt-data voorkomen (klicThemas). Zolang die nog
// niet bekend is (null), of leeg, tonen we het volledige schema als terugval.
function KlicLegenda({ klicThemas }) {
  const themas = (klicThemas && klicThemas.size)
    ? KLIC_THEMAS.filter(t => klicThemas.has(t.thema))
    : KLIC_THEMAS;
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600}>KLIC — kabels & leidingen</Text>
      {themas.map(({ label, kleur }) => (
        <Group key={label} gap={6} wrap="nowrap">
          <span style={{
            display: 'inline-block', width: 16, height: 4, borderRadius: 2,
            background: kleur, flexShrink: 0,
          }} />
          <Text size="xs">{label}</Text>
        </Group>
      ))}
    </Stack>
  );
}

// Legenda rechtsonder die naar boven uitklapt. Vervangt de niveau-badge
// (het kruimelpad bovenin toont het niveau al) en de legendabalken onderin
// die met de "Belangentafel starten"-knop botsten.
export default function LegendaPaneel({
  level, isStedin, jaren, minJaar, maxJaar, selectedYear, onYearClick, regioJaarActief,
  klicThemas,
}) {
  const [open, setOpen] = useState(false);

  // Jaartallen-legenda op gemeenteniveau, en op regioniveau als de jaar-toggle
  // (RegioSidebar) aanstaat — dan voor de hele regio.
  const toonJaren = (level === 'gemeente' || (level === 'regio' && regioJaarActief))
    && isStedin && jaren && jaren.length > 0;
  const toonStatus = level === 'buurt';
  const toonKlic = level === 'project';
  if (!toonJaren && !toonStatus && !toonKlic) return null;

  return (
    <Stack
      gap={6} align="flex-end"
      // Boven de MapLibre-attributieknop (i) die rechtsonder in de hoek zit
      style={{ position: 'absolute', bottom: 48, right: 10, zIndex: 5 }}
    >
      {open && (
        <Paper shadow="md" radius="md" withBorder p="sm">
          {toonJaren && (
            <JarenLegenda
              jaren={jaren} minJaar={minJaar} maxJaar={maxJaar}
              selectedYear={selectedYear} onYearClick={onYearClick}
            />
          )}
          {toonStatus && <StatusLegenda />}
          {toonKlic && <KlicLegenda klicThemas={klicThemas} />}
        </Paper>
      )}
      <Button
        variant="default" size="xs" radius="md"
        onClick={() => setOpen(o => !o)}
        rightSection={<span style={{ fontSize: 10 }}>{open ? '▼' : '▲'}</span>}
      >
        Legenda
      </Button>
    </Stack>
  );
}
